import { useEffect, useState } from "react";
import {
  Modal,
  Input,
  DatePicker,
  Row,
  Col,
  message,
  TimePicker,
  Button,
} from "antd";
import { EditModalProps, EventType } from "../../../../types/events.type";
import { useForm, Controller } from "react-hook-form";
import dayjs from "dayjs";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { handleApiError } from "../../../../core/api/instanceApi";
import {
  DeleteCalendarEvent,
  AddCalendarEvent,
} from "../../../../core/api/calendarEventsApi";
import {
  CALENDAR_EVENT_DEFAULT_VALUE,
  DATE_TIME_FORMAT,
  DUMMY_DATE,
} from "../constant";
import { toast } from "react-toastify";
import FormItem from "./FormItem";
import RichTextEditor from "./RichTextEditor";
import { useTranslation } from "react-i18next";
import { CustomEditor } from "../../../../core/common/ults/editor";
import { domParser } from "../../../../core/common/ults/domParser";

const EditModal = ({
  isModalOpen,
  setIsModalOpen,
  selectedEvent,
  setSelectedEvent,
  isViewModal,
}: any) => {
  const { t } = useTranslation();
  const {
    control,
    // setValue,
    // handleSubmit,
    getValues,
    reset,
    formState: { isValid },
  } = useForm<EventType>({
    defaultValues: CALENDAR_EVENT_DEFAULT_VALUE,
  });

  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    reset(selectedEvent);
  }, [reset, selectedEvent]);

  useEffect(() => {
    if (!isModalOpen) {
      reset(CALENDAR_EVENT_DEFAULT_VALUE);
      setSelectedEvent(CALENDAR_EVENT_DEFAULT_VALUE);
      setEditMode(false);
    }
  }, [isModalOpen, reset, setSelectedEvent]);

  const { mutate: deleteMutate, isPending: isDeleteLoading } = useMutation({
    mutationFn: async (id: string) => {
      return await DeleteCalendarEvent(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["Calendar-event"] });
      setIsModalOpen(false);
      setIsDeleteConfirmOpen(false);
      toast.success(t("student.events.deleteSuccess") as string);
    },
    onError: handleApiError,
  });

  const { mutate, isPending: isSubmitting } = useMutation({
    mutationFn: async (params: EventType) => {
      isViewModal && params?.id && (await DeleteCalendarEvent(params.id));
      return await AddCalendarEvent(params);
    },
    onSuccess: (response) => {
      if (response?.errorcode) {
        toast.error(response.message);
        return message.error(response.message);
      } else {
        queryClient.invalidateQueries({ queryKey: ["Calendar-event"] });
        setIsModalOpen(false);
        toast.success(t("student.events.createSuccess") as string);
      }
    },
    onError: handleApiError,
  });

  const handleOk = () => {
    let values = getValues();

    // Check some conditions
    if (
      values.start_time &&
      values.end_time &&
      new Date(values.start_time) > new Date(values.end_time)
    ) {
      toast.error(t("student.events.invalidPeriod") as string);
      return;
    }
    mutate(values);
  };

  const handleEdit = () => {
    setEditMode(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const onDeleteConfirm = () => {
    selectedEvent?.id && deleteMutate(selectedEvent.id);
  };

  const getReadableEventType = (event: any) => {
    const key = event?.modulename || event?.eventtype || "user";
    return t(`student.events.types.${key}`);
  };

  return (
    <>
      <Modal
        title={`${
          isViewModal
            ? t("student.events.eventDetails")
            : t("student.events.addEvent")
        }`}
        closable={{ "aria-label": "Custom Close Button" }}
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        centered
      >
        <div className="edit-calendar-event-wrapper">
          <form>
            <Row>
              <Col span={24}>
                <Controller
                  name="title"
                  control={control}
                  rules={{ required: t("student.events.titleRequired") }}
                  render={({ field }) => (
                    <FormItem
                      title={t("student.events.content")}
                      required={!(isViewModal && !editMode)}
                    >
                      <>
                        {isViewModal && !editMode ? (
                          <span>{field.value}</span>
                        ) : (
                          <Input
                            {...field}
                            placeholder={t("student.events.title")}
                          />
                        )}
                      </>
                    </FormItem>
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col span={24}>
                <FormItem title={t("student.events.eventType")}>
                  <>
                    {isViewModal && !editMode ? (
                      <span>{getReadableEventType(selectedEvent)}</span>
                    ) : (
                      <Input disabled value={t("student.events.userEvent")} />
                    )}
                  </>
                </FormItem>
              </Col>
            </Row>
            <Row gutter={10}>
              <Col span={24}>
                <Controller
                  name="start"
                  control={control}
                  defaultValue=""
                  rules={{ required: t("student.events.dateRequired") }}
                  render={({ field }) => (
                    <FormItem
                      title={t("student.events.eventDate")}
                      required={!(isViewModal && !editMode)}
                    >
                      {isViewModal && !editMode ? (
                        // Hiển thị kết hợp start + end nếu là dữ liệu từ Moodle
                        <span className="m-1">
                          {selectedEvent?.start &&
                          selectedEvent?.end &&
                          dayjs(selectedEvent.end).isValid()
                            ? `${dayjs(selectedEvent.start).format(
                                " DD/MM/YYYY HH:mm"
                              )} → ${dayjs(selectedEvent.end).format(
                                "DD/MM/YYYY HH:mm"
                              )}`
                            : dayjs(field.value).format("DD-MM-YYYY")}
                        </span>
                      ) : (
                        <DatePicker
                          className="w-full"
                          value={field.value && dayjs(field.value)}
                          onChange={(date) => {
                            field.onChange(dayjs(date).format("YYYY-MM-DD")); // dùng format chuẩn
                          }}
                          placeholder={t("student.events.eventDate")}
                        />
                      )}
                    </FormItem>
                  )}
                />
              </Col>
            </Row>
            {!(isViewModal && !editMode) && (
              <Row gutter={10}>
                <Col span={12}>
                  <Controller
                    name="start_time"
                    control={control}
                    rules={{ required: t("student.events.startTimeRequired") }}
                    render={({ field }) => (
                      <FormItem
                        title={t("student.events.startTime")}
                        required={true}
                      >
                        <TimePicker
                          format={DATE_TIME_FORMAT.HH_mm}
                          minuteStep={15}
                          showNow={false}
                          value={
                            field.value
                              ? dayjs(`${DUMMY_DATE} ${field.value}`)
                              : null
                          }
                          className="w-full"
                          onChange={(time) =>
                            field.onChange(
                              dayjs(time).format(DATE_TIME_FORMAT.HH_mm)
                            )
                          }
                          defaultOpenValue={dayjs(
                            "00:00",
                            DATE_TIME_FORMAT.HH_mm
                          )}
                        />
                      </FormItem>
                    )}
                  />
                </Col>
                <Col span={12}>
                  <Controller
                    name="end_time"
                    control={control}
                    render={({ field }) => (
                      <FormItem title={t("student.events.endTime")}>
                        <TimePicker
                          format={DATE_TIME_FORMAT.HH_mm}
                          minuteStep={15}
                          showNow={false}
                          value={
                            field.value
                              ? dayjs(`${DUMMY_DATE} ${field.value}`)
                              : null
                          }
                          className="w-full"
                          onChange={(time) =>
                            field.onChange(
                              dayjs(time).format(DATE_TIME_FORMAT.HH_mm)
                            )
                          }
                          defaultOpenValue={dayjs(
                            "00:00",
                            DATE_TIME_FORMAT.HH_mm
                          )}
                        />
                      </FormItem>
                    )}
                  />
                </Col>
              </Row>
            )}
            <Row>
              <Col span={24}>
                <Controller
                  name="description"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <FormItem title={t("student.events.description")}>
                      {isViewModal && !editMode ? (
                        <p
                          style={{
                            maxHeight: field.value ? "200px" : "auto",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            width: "100%",
                            overflowY: field.value ? "scroll" : "visible",
                          }}
                          dangerouslySetInnerHTML={{
                            __html: domParser(
                              field.value || t("courseDetails.noDescription")
                            ),
                          }}
                        />
                      ) : (
                        <div style={{ width: "80%" }}>
                          <CustomEditor
                            value={field?.value || ""}
                            onEditorChange={(value: any) =>
                              field.onChange(value)
                            }
                            id="message"
                            disabled={isViewModal && !editMode}
                          />
                        </div>
                      )}
                    </FormItem>
                  )}
                />
              </Col>
            </Row>
          </form>
          <div className="buttons-container">
            <Button onClick={handleCancel}>{t("student.events.cancel")}</Button>
            {isViewModal && selectedEvent?.eventtype === "user" && (
              <Button
                onClick={() => setIsDeleteConfirmOpen(true)}
                type="primary"
                danger
              >
                {t("student.events.delete")}
              </Button>
            )}
            {editMode ? (
              <Button onClick={handleOk} type="primary">
                {t("student.events.save")}
              </Button>
            ) : (
              (!selectedEvent?.eventtype ||
                selectedEvent?.eventtype === "user") && (
                <Button
                  onClick={!isViewModal ? handleOk : handleEdit}
                  disabled={!isValid || isSubmitting}
                  type="primary"
                >
                  {` ${isViewModal ? t("student.events.edit") : "Add"}`}
                </Button>
              )
            )}
          </div>
        </div>
      </Modal>
      <Modal
        title={t("student.events.delete")}
        open={isDeleteConfirmOpen}
        footer={null}
        centered
      >
        <p>{t("student.events.deleteConfirm")}</p>
        <div className="buttons-container">
          <Button onClick={() => setIsDeleteConfirmOpen(false)}>
            {t("student.events.no")}
          </Button>
          <Button onClick={onDeleteConfirm} type="primary" danger>
            {t("student.events.delete")}
          </Button>
        </div>
      </Modal>
    </>
  );
};

export default EditModal;
